import React from 'react';
import { Box, Skeleton } from '@mui/material';
import { House, Palmtree, Thermometer } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { differenceInMonths, parseISO } from 'date-fns';
import { StatusPill, useDelayedFlag } from '../../components/ui';
import type { StatusTone } from '../../components/ui';
import { sbg } from '../../theme/sbgTokens';
import { formatCount, formatDate, formatNumber, MISSING } from '../../utils/format';
import type { AllocationPace } from '../../utils/timeOffUsage';
import { TimeOffCategory } from '../../types/timeOff';

/** Hours with one decimal at most and a unit: "8 hrs", "1 hr", "2.5 hrs", "−6 hrs". */
export const hrs = (value: number | null | undefined) => {
  if (value === null || value === undefined || !Number.isFinite(value)) return MISSING;
  const rounded = Math.round((value + Number.EPSILON) * 10) / 10;
  const text = formatNumber(rounded, Number.isInteger(rounded) ? 0 : 1).replace(/^-/, '−');
  return `${text} ${Math.abs(rounded) === 1 ? 'hr' : 'hrs'}`;
};

/* ------------------------------------------------------------------------ */
/* Time-off types                                                             */
/* ------------------------------------------------------------------------ */

export const TYPE_META: Record<string, { label: string; Icon: LucideIcon }> = {
  PTO: { label: 'PTO', Icon: Palmtree },
  SICK: { label: 'Sick time', Icon: Thermometer },
  WFH: { label: 'WFH', Icon: House },
};

export const typeLabel = (type: string) => TYPE_META[type]?.label ?? type;

export const TypeLabel: React.FC<{ type: string }> = ({ type }) => {
  const meta = TYPE_META[type];
  const Icon = meta?.Icon;
  return (
    <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75, whiteSpace: 'nowrap' }}>
      {Icon && (
        <Box component="span" aria-hidden="true" sx={{ display: 'inline-flex', color: sbg.text.secondary }}>
          <Icon size={16} />
        </Box>
      )}
      {meta?.label ?? type}
    </Box>
  );
};

export const CATEGORY_LABELS: Record<string, string> = {
  [TimeOffCategory.VACATION]: 'Vacation or personal time',
  [TimeOffCategory.MEDICAL]: 'Medical or health',
  [TimeOffCategory.WFH]: 'Work from home',
  [TimeOffCategory.BUSINESS_TRAVEL]: 'Business travel',
  [TimeOffCategory.FAMILY_EVENT]: 'Family event',
  [TimeOffCategory.OTHER]: 'Other',
};

/* ------------------------------------------------------------------------ */
/* Statuses                                                                   */
/* ------------------------------------------------------------------------ */

const sentenceCase = (value: string) => {
  const text = value.replace(/_/g, ' ').trim().toLowerCase();
  return text ? text[0].toUpperCase() + text.slice(1) : MISSING;
};

const ENTRY_STATUS: Record<string, { tone: StatusTone; label: string }> = {
  APPROVED: { tone: 'success', label: 'Approved' },
  PENDING: { tone: 'warning', label: 'Pending' },
  REJECTED: { tone: 'danger', label: 'Rejected' },
};

export const EntryStatusPill: React.FC<{ status: string }> = ({ status }) => {
  const meta = ENTRY_STATUS[status] ?? { tone: 'neutral' as StatusTone, label: sentenceCase(status || '') };
  return <StatusPill tone={meta.tone}>{meta.label}</StatusPill>;
};

/** Pace states. Within pace carries no pill: color only marks what needs attention. */
export const PACE_META: Record<AllocationPace['status'], { tone: StatusTone | null; label: string; detail: string }> = {
  'over-allotment': {
    tone: 'danger',
    label: 'Over allotment',
    detail: 'Approved time is more than the allotment.',
  },
  'over-pace': {
    tone: 'warning',
    label: 'Pacing over',
    detail: 'At the current pace, usage passes the allotment by Dec 31.',
  },
  'pending-risk': {
    tone: 'warning',
    label: 'Pending risk',
    detail: 'Approving the pending requests would pass the allotment.',
  },
  'no-allotment': {
    tone: 'neutral',
    label: 'No allotment',
    detail: 'No hours are allotted for this category.',
  },
  'within-pace': {
    tone: null,
    label: 'Within pace',
    detail: 'Projected usage stays within the allotment.',
  },
};

/** WFH escalation review reasons, as a status a person can read. */
export const ESCALATION_META: Record<string, { tone: StatusTone; label: string }> = {
  wfh_not_approved: { tone: 'warning', label: 'Awaiting employee action' },
  wfh_escalated_to_vp: { tone: 'warning', label: 'Pending VP review' },
  wfh_vp_approved: { tone: 'success', label: 'VP approved · leads off' },
  wfh_vp_approved_with_leads: { tone: 'success', label: 'VP approved · leads on' },
  wfh_vp_declined: { tone: 'danger', label: 'VP declined' },
  wfh_hr_approved: { tone: 'success', label: 'HR approved · leads off' },
  wfh_hr_declined: { tone: 'danger', label: 'HR declined' },
  wfh_rescinded: { tone: 'neutral', label: 'Withdrawn' },
};

export const escalationMeta = (reason: string) =>
  ESCALATION_META[reason] ?? { tone: 'neutral' as StatusTone, label: sentenceCase(reason) };

export const EMPLOYMENT_STATUS_LABELS: Record<string, string> = {
  active: 'Active',
  terminated: 'Terminated',
  resigned_regrettable: 'Resigned (regrettable)',
  resigned_unregrettable: 'Resigned (unregrettable)',
};

/* ------------------------------------------------------------------------ */
/* Tenure                                                                     */
/* ------------------------------------------------------------------------ */

/** "Started Mar 4, 2024 (2 yrs)" · "Starts Nov 3, 2026" · null when unknown. */
export const formatStartLine = (startDate: string | null | undefined, todayYmd: string) => {
  const startKey = (startDate || '').slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(startKey)) return null;
  const start = parseISO(startKey);
  const today = parseISO(todayYmd);
  if (Number.isNaN(start.getTime()) || Number.isNaN(today.getTime())) return null;
  if (startKey > todayYmd) return `Starts ${formatDate(startKey)}`;
  const months = differenceInMonths(today, start);
  const tenure = months < 1
    ? 'under a month'
    : months < 12
      ? formatCount(months, 'mo', 'mos')
      : formatCount(Math.floor(months / 12), 'yr', 'yrs');
  return `Started ${formatDate(startKey)} (${tenure})`;
};

/* ------------------------------------------------------------------------ */
/* Region skeleton                                                            */
/* ------------------------------------------------------------------------ */

/** Skeleton blocks at roughly the real size, revealed after 300ms so fast loads never flash. */
export const RegionSkeleton: React.FC<{ label?: string; rows?: number; height?: number; sx?: object }> = ({
  label,
  rows = 3,
  height = 20,
  sx,
}) => {
  const visible = useDelayedFlag(true, 300);
  return (
    <Box
      role={label ? 'status' : undefined}
      aria-label={label}
      aria-busy={label ? 'true' : undefined}
      sx={{ display: 'grid', gap: 1.5, ...sx }}
    >
      {label && <span className="sbg-sr-only">{label}</span>}
      {Array.from({ length: rows }).map((_, index) => (
        <Skeleton
          key={index}
          aria-hidden="true"
          variant="rounded"
          height={height}
          width={index === 0 ? '60%' : `${88 - index * 12}%`}
          sx={{ visibility: visible ? 'visible' : 'hidden' }}
        />
      ))}
    </Box>
  );
};
